import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

interface Star {
    x: number;
    y: number;
    z: number;
    radius: number;
    opacity: number;
}

interface InactiveStarsProps {
    isActive: boolean;
}

export default function InactiveStars({ isActive }: InactiveStarsProps) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const requestRef = useRef<number | null>(null);
    const starsRef = useRef<Star[]>([]);

    // Initialize stars
    useEffect(() => {
        if (!isActive || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initStars();
        };

        const initStars = () => {
            const stars: Star[] = [];
            const numStars = 200;
            for (let i = 0; i < numStars; i++) {
                stars.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    z: Math.random() * 2, // Depth factor for parallax/speed
                    radius: Math.random() * 1.5,
                    opacity: Math.random(),
                });
            }
            starsRef.current = stars;
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);

        const animate = () => {
            if (!ctx || !canvas) return;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Update and draw stars
            starsRef.current.forEach((star) => {
                // Move star
                star.y -= 0.5 * star.z; // Move upwards, speed based on depth

                // Reset if off screen
                if (star.y < 0) {
                    star.y = canvas.height;
                    star.x = Math.random() * canvas.width;
                }

                // Draw star
                ctx.beginPath();
                ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(255, 255, 255, ' + star.opacity + ')';
                ctx.fill();

                // Twinkle effect
                star.opacity += (Math.random() - 0.5) * 0.05;
                if (star.opacity < 0.1) star.opacity = 0.1;
                if (star.opacity > 1) star.opacity = 1;
            });

            requestRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener("resize", resizeCanvas);
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [isActive]);

    if (!isActive) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 9999, // Behind most content but visible on background
                pointerEvents: "none", // Allow clicks to pass through
                opacity: isActive ? 1 : 0,
                transition: "opacity 1s ease-in-out",
                background: "transparent",
            }}
        >
            <canvas
                ref={canvasRef}
                style={{ width: "100%", height: "100%", display: "block" }}
            />
        </Box>
    );
}
